import { type GamePageContext } from "@/features/game/state";
import { onePagerCoworldOf } from "@/features/play/state";
import { getActualViewer } from "@/data/viewer";
import { flagOn } from "@/data/flags";

import { readStorePagePermissions } from "@/data/store-page-permissions";
import { viewerProgress } from "@/data/viewer-progress";

import { observatoryFlags } from "@softmax/web-features/flags";
import { buildHref } from "@softmax/web-addresses";

import {
  searchStringOf,
  type PreviewSearch,
  type FrontDoorViewerResult,
} from "./front-door";
import {
  viewerHasLiveEntry,
  viewerHasLiveEntryInLeague,
} from "./membership.data";

export async function readFrontDoorViewer({
  signal,
  context,
  search,
}: {
  signal?: AbortSignal | undefined;
  context: GamePageContext;
  search: PreviewSearch;
}): Promise<FrontDoorViewerResult> {
  const leagueSlug = context.league?.requestedSlug ?? undefined;
  const route = { game: context.address, league: leagueSlug };
  const query = searchStringOf(search);
  const corpus = [context.corpusSlug, leagueSlug].filter(Boolean).join("/");
  const defaultDoor = () =>
    buildHref({
      name: onePagerCoworldOf(corpus) ? "game.play" : "game.start",
      ...route,
      search: new URLSearchParams(query),
    });
  const viewer = await getActualViewer({ signal });

  if (!viewer)
    return {
      viewer: null,
      isMember: false,
      inProgress: false,
      canEdit: false,
      canReview: false,
      doorHref: defaultDoor(),
    };

  const permissions = !leagueSlug
    ? Promise.all([
        flagOn({
          signal,
          key: observatoryFlags.storePageEditor,
        }),
        readStorePagePermissions({ signal, coworld: context.corpusSlug }),
      ])
    : Promise.resolve(null);
  const [isMember, progress, editor] = await Promise.all([
    leagueSlug
      ? viewerHasLiveEntryInLeague({
          signal,
          slug: corpus,
          context,
        })
      : viewerHasLiveEntry({
          signal,
          slug: corpus,
          context,
        }),
    viewerProgress({ signal, context }),
    permissions,
  ]);
  const inProgress =
    !isMember && !leagueSlug && progress?.has(context.address.slug) === true;
  let doorHref = defaultDoor();

  if (isMember || inProgress || onePagerCoworldOf(corpus)) {
    const progressDoor = leagueSlug
      ? undefined
      : progress?.get(context.address.slug);
    doorHref = progressDoor
      ? `${progressDoor}${query}`
      : buildHref({
          name: "game.play",
          ...route,
          search: new URLSearchParams(query),
        });
  }

  const canEdit = editor !== null && editor[0] && editor[1].can_edit;
  return {
    viewer,
    isMember,
    inProgress,
    doorHref,
    canEdit,
    canReview: canEdit && viewer.is_softmax_team_member === true,
  };
}
